import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Header from "./Header";
import Aside from "./Aside";
import Footer from "./Footer";
import { useAppStore } from "../lib/store/useAppStore";

const APP_ASIDE_COLLAPSED_STORAGE_KEY = "checkLab.appAsideCollapsed";

const getIsCompactLayout = () =>
  typeof window !== "undefined" ? window.innerWidth < 1024 : false;

const getStoredIsAsideCollapsed = () => {
  if (typeof window === "undefined") return false;

  try {
    return (
      window.localStorage.getItem(APP_ASIDE_COLLAPSED_STORAGE_KEY) === "true"
    );
  } catch {
    return false;
  }
};

export default function Container() {
  const { pathname, search } = useLocation();
  const [isCompactLayout, setIsCompactLayout] = useState(getIsCompactLayout);
  const [isAsideCollapsed, setIsAsideCollapsed] = useState(
    getStoredIsAsideCollapsed,
  );
  const last_selected_project = useAppStore(
    (s) => s.user?.last_selected_project ?? null,
  );
  const isAdminRoute = pathname.startsWith("/admin");
  const isChildOrganizationProjectView =
    pathname.startsWith("/project/") &&
    new URLSearchParams(search).get("organizationScope") === "child";
  const shouldRenderHeader = isAdminRoute || !isCompactLayout;
  const shouldApplyAsideCollapsed =
    !isChildOrganizationProjectView &&
    isAsideCollapsed &&
    (isAdminRoute || !isCompactLayout);

  useEffect(() => {
    const handleResize = () => setIsCompactLayout(getIsCompactLayout());

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        APP_ASIDE_COLLAPSED_STORAGE_KEY,
        String(isAsideCollapsed),
      );
    } catch {
      // localStorage can be unavailable in private or restricted browser modes.
    }
  }, [isAsideCollapsed]);

  return (
    <div
      className={[
        "container",
        isAdminRoute ? "container--admin" : "",
        isChildOrganizationProjectView ? "container--asideHidden" : "",
        shouldApplyAsideCollapsed ? "container--asideCollapsed" : "",
        shouldRenderHeader ? "" : "container--headerless",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {shouldRenderHeader && (
        <Header
          isAsideCollapsed={shouldApplyAsideCollapsed}
          onAsideCollapsedChange={setIsAsideCollapsed}
          showAsideToggle={!isChildOrganizationProjectView}
        />
      )}
      {!isChildOrganizationProjectView && (
        <Aside
          key={last_selected_project}
          isCollapsed={shouldApplyAsideCollapsed}
        />
      )}
      <Outlet />
      <Footer />
    </div>
  );
}
